import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../../../utils/api';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface Organization {
  organization_id: string;
  name: string;
  organization_code: string;
}

// Full user interface from the API
interface ApiUser {
  user_id: string;
  username: string;
  email: string;
  organization_id: string;
  role: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// User data for form submissions
interface UserFormData {
  user_id?: string;
  username: string;
  email: string;
  password?: string;
  organization_id: string;
  role: string;
  is_active?: boolean;
}

interface V3UserFormProps {
  user?: ApiUser;
  onSubmit: (userData: UserFormData) => Promise<void>;
  onClose: () => void;
}

const V3UserForm: React.FC<V3UserFormProps> = ({ user, onSubmit, onClose }) => {
  const [userData, setUserData] = useState<UserFormData>({
    username: '',
    email: '',
    password: '',
    organization_id: '',
    role: 'user',
  });
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const isEditMode = !!user?.user_id;
  
  // Load organizations
  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const data = await fetchWithAuth('/v1/auth/admin/organizations');
        setOrganizations(data);
        
        // If this is a new user and we have organizations, set the first one as default
        if (!isEditMode && data.length > 0 && !userData.organization_id) {
          setUserData(prev => ({
            ...prev,
            organization_id: data[0].organization_id
          }));
        }
      } catch (err) {
        setError('Failed to load organizations');
        console.error(err);
      }
    };
    
    fetchOrganizations();
  }, []);
  
  // If editing, load user data
  useEffect(() => {
    if (user) {
      setUserData({
        user_id: user.user_id,
        username: user.username,
        email: user.email,
        organization_id: user.organization_id,
        role: user.role,
        is_active: user.is_active !== false
      });
    }
  }, [user]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setUserData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setUserData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await onSubmit(userData);
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error(err);
      setLoading(false);
    }
  };
  
  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">
              {isEditMode ? 'Edit User' : 'Create New User'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6">
            {error && (
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg text-red-800 mb-6">
                {error}
              </div>
            )}
            
            <div className="space-y-5">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                  Username <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={userData.username}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={userData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password {!isEditMode && <span className="text-gray-500 text-xs">(Optional)</span>}
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={userData.password || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder={isEditMode ? "Leave blank to keep current password" : "Leave blank to send magic link for password setup"}
                />
                {!isEditMode && (
                  <p className="mt-1 text-xs text-gray-500">
                    Leave blank to send magic link for password setup
                  </p>
                )}
              </div>
              
              <div>
                <label htmlFor="organization_id" className="block text-sm font-medium text-gray-700 mb-1">
                  Organization {!isEditMode && <span className="text-red-500">*</span>}
                  {isEditMode && <span className="text-gray-500 text-xs ml-1">(Cannot be changed)</span>}
                </label>
                <select
                  id="organization_id"
                  name="organization_id"
                  value={userData.organization_id}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                    isEditMode ? 'bg-gray-50 cursor-not-allowed' : ''
                  }`}
                  required
                  disabled={isEditMode}
                >
                  <option value="">Select an organization</option>
                  {organizations.map(org => (
                    <option key={org.organization_id} value={org.organization_id}>
                      {org.name} ({org.organization_code})
                    </option>
                  ))}
                </select>
                {isEditMode && (
                  <p className="mt-1 text-xs text-gray-500">
                    Organization cannot be changed once set
                  </p>
                )}
              </div>
              
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  id="role"
                  name="role"
                  value={userData.role}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  required
                >
                  <option value="user">User</option>
                  <option value="admin">Administrator</option>
                </select>
              </div>
              
              {isEditMode && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={userData.is_active}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                    Active account
                  </label>
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:bg-blue-400"
                disabled={loading}
              >
                {loading ? 'Saving...' : isEditMode ? 'Update User' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default V3UserForm;